import { defineStore } from 'pinia';
import { useDataStore } from './data.js';
import { router } from '../router.js';

import gql_query_project_tags from '../graphql/query/ProjectTags.gql';
import gql_query_common_project_tags from '../graphql/query/CommonProjectTags.gql';
import gql_query_project_locations from '../graphql/query/ProjectLocations.gql';
import gql_query_offices from '../graphql/query/Offices.gql';
import gql_query_project_types from '../graphql/query/ProjectTypes.gql';
import gql_query_default_project_filters from '../graphql/query/DefaultProjectFilters.gql';
import gql_query_common_options from '../graphql/query/CommonOptions.gql';
import gql_query_companies from '../graphql/query/Companies.gql';
import gql_query_media_types from '../graphql/query/MediaTypes.gql';

const formatDropdownFilterOptions = ( data, options ) => {
	const label_key = options && options.label_key ? options.label_key : 'title';
	const value_key = options && options.value_key ? options.value_key : 'id';
	return data.map( element => {
		return {
			label: element[label_key],
			value: element[value_key]
		}
	} );
};

const normaliseString = ( string ) => {
	return string.normalize( 'NFD' ).replace( /[\u0300-\u036f]/g, '' );
}

const filterResultsByQuery = ( results, query ) => {
	const normalised_query = normaliseString( query.toLowerCase() );
	return results.filter( item => {
		const normalised_item_label = normaliseString( item.label.toLowerCase() );
		return normalised_item_label.includes( normalised_query );
	} );
}

export const useProjectFiltersStore = defineStore( 'project-filters', { // eslint-disable-line snakecasejs/snakecasejs
	persist: false,
	state: () => {
		return {
			active_tab: 'my_projects',
			show_filters: false,
			dropdown_filter_components: [
				{
					value: 'project_location',
					label: 'Location',
					options_key: 'filtered_project_locations',
					get_options_method: 'getProjectLocations',
					selected: true
				},
				{
					value: 'project_status',
					label: 'Project Status',
					search_disabled: true,
					selected: true
				},
				{
					value: 'project_type',
					label: 'Project Type',
					get_options_method: 'getProjectTypes',
					search_disabled: true,
					selected: true
				},
				{
					value: 'published',
					label: 'Published',
					search_disabled: true,
					selected: true
				},
				{
					value: 'media_types',
					label: 'Deliverables',
					get_options_method: 'getMediaTypes',
					selected: false
				},
				{
					value: 'production_office',
					label: 'Production Office',
					get_options_method: 'getOffices',
					search_disabled: true,
					selected: true
				},
				{
					value: 'company',
					label: 'Company',
					options_key: 'filtered_companies',
					selected: true
				},
				{
					value: 'client',
					label: 'Client',
					options_key: 'filtered_clients',
					selected: true
				},
				{
					value: 'developer',
					label: 'Developer',
					options_key: 'filtered_developers',
					selected: false
				},
				{
					value: 'development_consultant',
					label: 'Development Consultant',
					options_key: 'filtered_development_consultants',
					selected: false
				},
				{
					value: 'brand_partner',
					label: 'Brand Partner',
					options_key: 'filtered_brand_partners',
					selected: false
				},
				{
					value: 'creative_agency',
					label: 'Creative Agency',
					options_key: 'filtered_creative_agencies',
					selected: false
				},
				{
					value: 'sales_marketing',
					label: 'Sales & Marketing',
					options_key: 'filtered_sales_marketing',
					selected: true
				},
				{
					value: 'hospitality_operator',
					label: 'Hospitality Operator',
					options_key: 'filtered_hospitality_operators',
					selected: false
				},
				{
					value: 'architect',
					label: 'Architect',
					options_key: 'filtered_architects',
					selected: true
				},
				{
					value: 'landscape_architect',
					label: 'Landscape Architect',
					options_key: 'filtered_landscape_architects',
					selected: true
				},
				{
					value: 'interior_design',
					label: 'Interior Design',
					options_key: 'filtered_interior_designers',
					selected: false
				},
				{
					value: 'project_manager',
					label: 'Project Manager',
					options_key: 'filtered_project_managers',
					selected: false
				},
				{
					value: 'client_services',
					label: 'Client Services',
					options_key: 'filtered_client_services',
					selected: true
				},
				{
					value: 'lead_artist',
					label: 'Lead Artist',
					options_key: 'filtered_lead_artists',
					selected: true
				},
				{
					value: 'artist',
					label: 'Artist',
					options_key: 'filtered_artists',
					selected: true
				},
				{
					value: 'business_development',
					label: 'Business Development',
					options_key: 'filtered_business_development',
					selected: false
				},
				{
					value: 'completion_date',
					label: 'Completion Date',
					search_disabled: true,
					selected: true
				}
			],
			search_query: '',
			pagination: {
				limit: 20,
				page: 1,
				total_count: null
			},
			sorting: {
				selected: 'name_asc',
				options: [
					{ label: 'Name A-Z', value: 'name_asc' },
					{ label: 'Name Z-A', value: 'name_desc' },
					{ label: 'Newest first', value: 'newest_first' },
					{ label: 'Oldest first', value: 'oldest_first' },
				]
			},
			date: {
				selected: null,
				options: [
					{ label: 'All time', value: null },
					{ label: 'Last 7 days', value: 'last_7_days' },
					{ label: 'Last 30 days', value: 'last_30_days' },
					{ label: 'Last 90 days', value: 'last_90_days' },
					{ label: 'Last year', value: 'last_year' }
				],
				custom: null
			},
			project_status: {
				options: [
					{ label: 'Active', value: 'active' },
					{ label: 'On Hold', value: 'on_hold' },
					{ label: 'Completed', value: 'completed' },
				],
				active: []
			},
			published: {
				options: [
					{ label: 'Published', value: true },
					{ label: 'Not Published', value: false },
				],
				active: []
			},
			completion_date: {
				options: [
					{ label: 'Next week', value: 'next_week' },
					{ label: 'Next 2 weeks', value: 'next_2_weeks' },
					{ label: 'Next month', value: 'next_month' },
					{ label: 'Next 3 months', value: 'next_3_months' },
				],
				active: []
			},
			media_types: {
				options: [],
				active: []
			},
			project_type: {
				options: [],
				active: []
			},
			tags: {
				search_query: '',
				common_tags: [],
				tags: [],
				active: [],
				total_count: null,
			},
			project_location: {
				search_query: '',
				common_options: [],
				options: [],
				active: []
			},
			production_office: {
				options: [],
				active: []
			},
			boundary_staff_users: [],
			project_manager: {
				search_query: '',
				common_options: [],
				active: [],
			},
			client_services: {
				search_query: '',
				common_options: [],
				active: [],
			},
			lead_artist: {
				search_query: '',
				common_options: [],
				active: [],
			},
			artist: {
				search_query: '',
				common_options: [],
				active: [],
			},
			business_development: {
				search_query: '',
				common_options: [],
				active: [],
			},
			companies: [],
			company: {
				search_query: '',
				common_options: [],
				active: [],
			},
			client: {
				search_query: '',
				common_options: [],
				active: [],
			},
			brand_partner: {
				search_query: '',
				common_options: [],
				active: [],
			},
			developer: {
				search_query: '',
				common_options: [],
				active: [],
			},
			development_consultant: {
				search_query: '',
				common_options: [],
				active: [],
			},
			creative_agency: {
				search_query: '',
				common_options: [],
				active: [],
			},
			sales_marketing: {
				search_query: '',
				common_options: [],
				active: [],
			},
			hospitality_operator: {
				search_query: '',
				common_options: [],
				active: [],
			},
			architect: {
				search_query: '',
				common_options: [],
				active: [],
			},
			landscape_architect: {
				search_query: '',
				common_options: [],
				active: [],
			},
			interior_design: {
				search_query: '',
				common_options: [],
				active: [],
			},
		};
	},
	actions: {
		async getCommonOptions() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_common_options, {} );
				if ( !response || !response.data ) {
					return;
				}
				// Users
				this.client_services.common_options = formatDropdownFilterOptions( response.data.globalSet.common_client_services, { label_key: 'fullName' } );
				this.business_development.common_options = formatDropdownFilterOptions( response.data.globalSet.common_business_development, { label_key: 'fullName' } );
				this.lead_artist.common_options = formatDropdownFilterOptions( response.data.globalSet.common_lead_artists, { label_key: 'fullName' } );
				this.artist.common_options = formatDropdownFilterOptions( response.data.globalSet.common_artists, { label_key: 'fullName' } );
				this.project_manager.common_options = formatDropdownFilterOptions( response.data.globalSet.common_project_managers, { label_key: 'fullName' } );

				// Locations
				this.project_location.common_options = formatDropdownFilterOptions( response.data.globalSet.common_project_locations );

				//Companies
				this.company.common_options = formatDropdownFilterOptions( response.data.globalSet.common_companies );
				this.architect.common_options = formatDropdownFilterOptions( response.data.globalSet.common_architects );
				this.brand_partner.common_options = formatDropdownFilterOptions( response.data.globalSet.common_brand_partners );
				this.client.common_options = formatDropdownFilterOptions( response.data.globalSet.common_clients );
				this.creative_agency.common_options = formatDropdownFilterOptions( response.data.globalSet.common_creative_agencies );
				this.developer.common_options = formatDropdownFilterOptions( response.data.globalSet.common_developers );
				this.development_consultant.common_options = formatDropdownFilterOptions( response.data.globalSet.common_development_consultants );
				this.hospitality_operator.common_options = formatDropdownFilterOptions( response.data.globalSet.common_hospitality_operators );
				this.interior_design.common_options = formatDropdownFilterOptions( response.data.globalSet.common_interior_designers );
				this.landscape_architect.common_options = formatDropdownFilterOptions( response.data.globalSet.common_landscape_architects );
				this.sales_marketing.common_options = formatDropdownFilterOptions( response.data.globalSet.common_sales_marketing );
			} catch ( error ) {
				return error;
			}
		},
		async getDefaultFilters() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_default_project_filters, {} );
				if ( !response || !response.data ) {
					return;
				}

				const selected_filters = response.data.globalSet?.default_project_filters ?? [];

				this.dropdown_filter_components.forEach( ( filter, i ) => {
					this.dropdown_filter_components[i].selected = selected_filters.includes( filter.value );
				} );
			} catch ( error ) {
				return error;
			}
		},
		setFilterSearchQuery( value, filter_handle ) {
			this[filter_handle].search_query = value;
		},
		setActiveFilterValue( value, filter_handle ) {
			this[filter_handle].active.push( value );
		},
		setActiveFilterValues( values, filter_handle ) {
			this[filter_handle].active = values;
		},
		removeActiveFilterValue( value, filter_handle ) {
			const index_to_remove = this[filter_handle].active.findIndex( active_value => active_value.value === value.value );
			if ( index_to_remove < 0 ) {
				return;
			}
			this[filter_handle].active.splice( index_to_remove, 1 );
		},
		setSearchQuery( value ) {
			this.search_query = value;
		},
		setActiveTab( slug ) {
			this.active_tab = slug;
		},
		setSelectedSorting( value ) {
			this.sorting.selected = value;
		},
		setSelectedDateRange( value, custom = false ) {
			this.date.selected = custom ? 'custom' : value;
			this.date.custom = custom ? value : null;
		},
		setActiveProjectStatuses( status_slugs ) {
			this.project_status.active = [];
			status_slugs.forEach( slug => {
				const status = this.project_status.options.find( status => status.value === slug );
				if ( status ) {
					this.project_status.active.push( status );
				}
			} );
		},
		setActivePublishedValues( values ) {
			this.published.active = [];
			values.forEach( value => {
				const option = this.published.options.find( option => option.value === value );
				if ( option ) {
					this.published.active.push( option );
				}
			} );
		},
		setActiveCompletionDateValues( values ) {
			this.completion_date.active = [];
			values.forEach( value => {
				const option = this.completion_date.options.find( option => option.value === value );
				if ( option ) {
					this.completion_date.active.push( option );
				}
			} );
		},
		async getBoundaryStaffUsers() {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers();
				if ( !response || !response.data ) {
					return;
				}
				this.boundary_staff_users = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getCompanies() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_companies, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.companies = formatDropdownFilterOptions( response.data.entries );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveProjectManagers( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers( ids );
				if ( !response || !response.data ) {
					return;
				}
				this.project_manager.active = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveAccountDirectors( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers( ids );
				if ( !response || !response.data ) {
					return;
				}
				this.client_services.active = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveLeadArtists( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers( ids );
				if ( !response || !response.data ) {
					return;
				}
				this.lead_artist.active = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveArtists( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers( ids );
				if ( !response || !response.data ) {
					return;
				}
				this.artist.active = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveBusinessDevelopers( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.getBoundaryStaffUsers( ids );
				if ( !response || !response.data ) {
					return;
				}
				this.business_development.active = formatDropdownFilterOptions( response.data.users, { label_key: 'fullName' } );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveCompanies( ids, slug ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_companies, { ids: ids } );
				if ( !response || !response.data ) {
					return;
				}
				this[slug].active = formatDropdownFilterOptions( response.data.entries, { label_key: 'title' } );
			} catch ( error ) {
				return error;
			}
		},
		async getCommonProjectLocations() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_common_project_locations, {} );
				if ( !response || !response.data ) {
					return;
				}
				this.project_location.common_options = formatDropdownFilterOptions( response.data.globalSet.common_project_locations );
			} catch ( error ) {
				return error;
			}
		},
		async getProjectLocations() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_locations, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.project_location.options = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveProjectLocations( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_locations, { ids: ids } );
				if ( !response || !response.data ) {
					return;
				}
				this.project_location.active = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getOffices() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_offices, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.production_office.options = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveOffices( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_offices, { ids: ids } );
				if ( !response || !response.data ) {
					return;
				}
				this.production_office.active = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getProjectTypes() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_types, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.project_type.options = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveProjectTypes( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_types, { ids: ids } );
				if ( !response || !response.data ) {
					return;
				}
				this.project_type.active = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getMediaTypes() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_media_types, { ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.media_types.options = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getActiveMediaTypes( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery(  gql_query_media_types, { ids: ids } );
				if ( !response || !response.data ) {
					return;
				}
				this.media_types.active = formatDropdownFilterOptions( response.data.categories );
			} catch ( error ) {
				return error;
			}
		},
		async getTags() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_tags, { search: this.tags.search_query, ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.tags.tags = response.data.tags;
			} catch ( error ) {
				return error;
			}
		},
		async getActiveTags( ids ) {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_tags, { search: null, ids: ids } );
				if ( !response || !response.data ) {
					return;
				}
				this.tags.active = response.data.tags;
			} catch ( error ) {
				return error;
			}
		},
		async getCommonTags() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_common_project_tags );
				if ( !response || !response.data ) {
					return;
				}
				this.tags.common_tags = response.data.globalSet.common_project_tags;
			} catch ( error ) {
				return error;
			}
		},
		async getTotalTagsCount() {
			const data_store = useDataStore();
			try {
				const response = await data_store.authenticatedQuery( gql_query_project_tags, { search: '', ids: null } );
				if ( !response || !response.data ) {
					return;
				}
				this.tags.total_count = response.data.tags.length;
			} catch ( error ) {
				return error;
			}
		},
		async getProjectStatusOptions() {
			const { isUserStaffAdmin, isUserStaffSuperAdmin } = useDataStore();
			try {
				let user_is_staff_admin = false;
				user_is_staff_admin = await isUserStaffAdmin();
				if ( !user_is_staff_admin ) {
					user_is_staff_admin = await isUserStaffSuperAdmin();
				}
				if ( !user_is_staff_admin ) {
					return;
				}

				this.project_status.options = [
					{ label: 'In-Setup', value: 'in_setup' },
					{ label: 'Inactive', value: 'inactive' },
					{ label: 'On Hold', value: 'on_hold' },
					{ label: 'Pending', value: 'pending' },
					{ label: 'Active', value: 'active' },
					{ label: 'Issue', value: 'issue' },
					{ label: 'At Risk', value: 'at_risk' },
					{ label: 'Suspended', value: 'suspended' },
					{ label: 'Terminated', value: 'terminated' },
					{ label: 'Completed', value: 'completed' },
				];
			} catch( error ) {
				return error;
			}
		},
		setActiveTag( tag ) {
			this.tags.active.push( tag );
		},
		setActiveTags( tags ) {
			this.tags.active = tags;
		},
		removeActiveTag( index ) {
			this.tags.active.splice( index, 1 );
		},
		setShowFilters( value ) {
			this.show_filters = value;
		},
		resetPagination() {
			this.pagination.page = 1;
		},
		incrementPagination() {
			this.pagination.page++;
		},
		updateTotalCount( value ) {
			this.pagination.total_count = value;
		},
		updateQueryStringFromFilters() {
			const query = {};
			query.tab = this.active_tab;

			if ( this.active_tab === 'example_projects' ) {
				this.show_filters = false;
				router.push( {
					name: 'projects',
					query
				} );
				return;
			}

			if ( this.sorting.selected ) {
				query.sort = this.sorting.selected;
			}
			if ( this.date.selected ) {
				if ( this.date.selected === 'custom' && this.date.custom.length === 2 ) {
					query.date_start = this.date.custom[0];
					query.date_end = this.date.custom[1];
				} else {
					query.period = this.date.selected;
				}
			}

			if ( this.search_query ) {
				query.search = this.search_query;
			}
			if ( this.active_tab === 'my_projects' ) {
				this.show_filters = false;
				router.push( {
					name: 'projects',
					query
				} );
				return;
			}

			if ( this.project_status.active.length ) {
				query.status = this.project_status.active.map( status => status.value );
			}
			if ( this.published.active.length ) {
				query.published = this.published.active.map( option => option.value );
			}
			if ( this.project_type.active.length ) {
				query.type = this.project_type.active.map( type => type.value );
			}
			if ( this.media_types.active.length ) {
				query.media_type = this.media_types.active.map( media_type => media_type.value );
			}
			if ( this.tags.active.length ) {
				query.tag = this.tags.active.map( tag => tag.id );
			}
			if ( this.project_location.active.length ) {
				query.location = this.project_location.active.map( location => location.value );
			}
			if ( this.production_office.active.length ) {
				query.production_office = this.production_office.active.map( office => office.value );
			}
			if ( this.completion_date.active.length ) {
				query.completion_date = this.completion_date.active.map( option => option.value );
			}
			if ( this.project_manager.active.length ) {
				query.project_manager = this.project_manager.active.map( user => user.value );
			}
			if ( this.client_services.active.length ) {
				query.client_services = this.client_services.active.map( user => user.value );
			}
			if ( this.lead_artist.active.length ) {
				query.lead_artist = this.lead_artist.active.map( user => user.value );
			}
			if ( this.artist.active.length ) {
				query.artist = this.artist.active.map( user => user.value );
			}
			if ( this.business_development.active.length ) {
				query.business_development = this.business_development.active.map( user => user.value );
			}
			if ( this.company.active.length ) {
				query.company = this.company.active.map( company => company.value );
			}
			if ( this.client.active.length ) {
				query.client = this.client.active.map( client => client.value );
			}
			if ( this.brand_partner.active.length ) {
				query.brand_partner = this.brand_partner.active.map( brand_partner => brand_partner.value );
			}
			if ( this.developer.active.length ) {
				query.developer = this.developer.active.map( developer => developer.value );
			}
			if ( this.development_consultant.active.length ) {
				query.development_consultant = this.development_consultant.active.map( development_consultant => development_consultant.value );
			}
			if ( this.creative_agency.active.length ) {
				query.creative_agency = this.creative_agency.active.map( creative_agency => creative_agency.value );
			}
			if ( this.sales_marketing.active.length ) {
				query.sales_marketing = this.sales_marketing.active.map( sales_marketing => sales_marketing.value );
			}
			if ( this.hospitality_operator.active.length ) {
				query.hospitality_operator = this.hospitality_operator.active.map( hospitality_operator => hospitality_operator.value );
			}
			if ( this.architect.active.length ) {
				query.architect = this.architect.active.map( architect => architect.value );
			}
			if ( this.landscape_architect.active.length ) {
				query.landscape_architect = this.landscape_architect.active.map( landscape_architect => landscape_architect.value );
			}
			if ( this.interior_design.active.length ) {
				query.interior_design = this.interior_design.active.map( interior_design => interior_design.value );
			}

			router.push( {
				name: 'projects',
				query
			} );
		},
		removeAllActiveDropdownFilters() {
			this.project_status.active = [];
			this.project_type.active = [];
			this.published.active = [];
			this.project_location.active = [];
			this.production_office.active = [];
			this.completion_date.active = [];
			this.media_types.active = [];
			this.project_manager.active = [];
			this.client_services.active = [];
			this.business_development.active = [];
			this.lead_artist.active = [];
			this.artist.active = [];
			this.company.active = [];
			this.client.active = [];
			this.creative_agency.active = [];
			this.brand_partner.active = [];
			this.developer.active = [];
			this.development_consultant.active = [];
			this.sales_marketing.active = [];
			this.hospitality_operator.active = [];
			this.architect.active = [];
			this.landscape_architect.active = [];
			this.interior_design.active = [];
		},
		resetActiveAdvancedFilters() {
			this.tags.active = [];
			this.removeAllActiveDropdownFilters();
		},
		setSelectedDropdownFilters( selection ) {
			this.dropdown_filter_components.forEach( filter => {
				filter.selected = selection.includes( filter.value );
			} );
		},
		removeUnselectedFilters() {
			let active_filters_changed = false;
			this.dropdown_filter_components.forEach( filter => {
				if ( !filter.selected ) {
					if ( this[filter.value].active.length ) {
						this[filter.value].active = [];
						active_filters_changed = true;
					}
				}
			} );
			return active_filters_changed;
		},
		selectMissingActiveDropdowns( selection ) {
			this.dropdown_filter_components.forEach( ( filter, index ) => {
				if ( selection.includes( filter.value ) ) {
					this.dropdown_filter_components[index].selected = true;
				}
			} );
		}
	},
	getters: {
		selected_dropdown_filter_components: ( state ) => {
			return state.dropdown_filter_components.filter( filter => filter.selected );
		},
		total_filter_count(state) {
			return state.tags.active.length + this.active_dropdown_filters.length;
		},
		active_dropdown_filters: ( state ) => {
			return [
				...state.project_status.active,
				...state.project_type.active,
				...state.published.active,
				...state.media_types.active,
				...state.project_location.active,
				...state.production_office.active,
				...state.completion_date.active,
				...state.project_manager.active,
				...state.client_services.active,
				...state.business_development.active,
				...state.lead_artist.active,
				...state.artist.active,
				...state.company.active,
				...state.client.active,
				...state.brand_partner.active,
				...state.developer.active,
				...state.development_consultant.active,
				...state.creative_agency.active,
				...state.sales_marketing.active,
				...state.hospitality_operator.active,
				...state.architect.active,
				...state.landscape_architect.active,
				...state.interior_design.active,
			];
		},
		filtered_project_locations: ( state ) => {
			if ( !state.project_location.search_query ) {
				if ( !state.project_location.common_options.length ) {
					return state.project_location.options;
				}
				return state.project_location.common_options;
			}
			return filterResultsByQuery( state.project_location.options, state.project_location.search_query );
		},
		filtered_client_services: ( state ) => {
			if ( !state.client_services.search_query ) {
				if ( !state.client_services.common_options.length ) {
					return state.boundary_staff_users;
				}
				return state.client_services.common_options;
			}
			return filterResultsByQuery( state.boundary_staff_users, state.client_services.search_query );
		},
		filtered_project_managers: ( state ) => {
			if ( !state.project_manager.search_query ) {
				if ( !state.project_manager.common_options.length ) {
					return state.boundary_staff_users;
				}
				return state.project_manager.common_options;
			}
			return filterResultsByQuery( state.boundary_staff_users, state.project_manager.search_query );
		},
		filtered_lead_artists: ( state ) => {
			if ( !state.lead_artist.search_query ) {
				if ( !state.lead_artist.common_options.length ) {
					return state.boundary_staff_users;
				}
				return state.lead_artist.common_options;
			}
			return filterResultsByQuery( state.boundary_staff_users, state.lead_artist.search_query );
		},
		filtered_artists: ( state ) => {
			if ( !state.artist.search_query ) {
				if ( !state.artist.common_options.length ) {
					return state.boundary_staff_users;
				}
				return state.artist.common_options;
			}
			return filterResultsByQuery( state.boundary_staff_users, state.artist.search_query );
		},
		filtered_business_developers: ( state ) => {
			if ( !state.business_development.search_query ) {
				if ( !state.business_development.common_options.length ) {
					return state.boundary_staff_users;
				}
				return state.business_development.common_options;
			}
			return filterResultsByQuery( state.boundary_staff_users, state.business_development.search_query );
		},
		filtered_companies: ( state ) => {
			if ( !state.company.search_query ) {
				if ( !state.company.common_options.length ) {
					return state.companies;
				}
				return state.company.common_options;
			}
			return filterResultsByQuery( state.companies, state.company.search_query );
		},
		filtered_clients: ( state ) => {
			if ( !state.client.search_query ) {
				if ( !state.client.common_options.length ) {
					return state.companies;
				}
				return state.client.common_options;
			}
			return filterResultsByQuery( state.companies, state.client.search_query );
		},
		filtered_brand_partners: ( state ) => {
			if ( !state.brand_partner.search_query ) {
				if ( !state.brand_partner.common_options.length ) {
					return state.companies;
				}
				return state.brand_partner.common_options;
			}
			return filterResultsByQuery( state.companies, state.brand_partner.search_query );
		},
		filtered_developers: ( state ) => {
			if ( !state.developer.search_query ) {
				if ( !state.developer.common_options.length ) {
					return state.companies;
				}
				return state.developer.common_options;
			}
			return filterResultsByQuery( state.companies, state.developer.search_query );
		},
		filtered_development_consultants: ( state ) => {
			if ( !state.development_consultant.search_query ) {
				if ( !state.development_consultant.common_options.length ) {
					return state.companies;
				}
				return state.development_consultant.common_options;
			}
			return filterResultsByQuery( state.companies, state.development_consultant.search_query );
		},
		filtered_creative_agencies: ( state ) => {
			if ( !state.creative_agency.search_query ) {
				if ( !state.creative_agency.common_options.length ) {
					return state.companies;
				}
				return state.creative_agency.common_options;
			}
			return filterResultsByQuery( state.companies, state.creative_agency.search_query );
		},
		filtered_sales_marketing: ( state ) => {
			if ( !state.sales_marketing.search_query ) {
				if ( !state.sales_marketing.common_options.length ) {
					return state.companies;
				}
				return state.sales_marketing.common_options;
			}
			return filterResultsByQuery( state.companies, state.sales_marketing.search_query );
		},
		filtered_hospitality_operators: ( state ) => {
			if ( !state.hospitality_operator.search_query ) {
				if ( !state.hospitality_operator.common_options.length ) {
					return state.companies;
				}
				return state.hospitality_operator.common_options;
			}
			return filterResultsByQuery( state.companies, state.hospitality_operator.search_query );
		},
		filtered_architects: ( state ) => {
			if ( !state.architect.search_query ) {
				if ( !state.architect.common_options.length ) {
					return state.companies;
				}
				return state.architect.common_options;
			}
			return filterResultsByQuery( state.companies, state.architect.search_query );
		},
		filtered_landscape_architects: ( state ) => {
			if ( !state.landscape_architect.search_query ) {
				if ( !state.landscape_architect.common_options.length ) {
					return state.companies;
				}
				return state.landscape_architect.common_options;
			}
			return filterResultsByQuery( state.companies, state.landscape_architect.search_query );
		},
		filtered_interior_designers: ( state ) => {
			if ( !state.interior_design.search_query ) {
				if ( !state.interior_design.common_options.length ) {
					return state.companies;
				}
				return state.interior_design.common_options;
			}
			return filterResultsByQuery( state.companies, state.interior_design.search_query );
		}
	}
} );
